// Implements: F-SHR-009, F-SHR-006, F-SHR-008, F-UIX-007, F-UIX-010
/**
 * The links page at `/sharing` (UX-08): the links the person created, across mockups, newest first, a
 * page at a time (`GET /api/sharing/links`, SB3), as a table with state tabs and an artifact filter.
 *
 * The server has no filters for this list, so the tabs and the artifact select narrow what is loaded;
 * the number on each tab is shown only once every page is loaded, because before that it would count
 * a part of the list and read as the whole. Each row shows what the server lists and **never** the
 * link itself: the URL is shown once, when the link is created (owner question 3), so the table
 * offers no copy, and the client keeps only the fields it shows (`toPanelLink`).
 *
 * Revoke and edit are offered only where the server's per-link `may_revoke` says so, never on a
 * revoked link. Revoke asks inside the page (`ConfirmDialog`, never a native dialog) and the row
 * changes without a reload, because every write refetches the lists. The edit of the expiry and the
 * password opens in a dialog; the label is renamed in its row (mockup v3 P8, the label alone in the
 * `PATCH`). An expired link offers no "Revocar" but can still change its label and expiry. An active
 * row's eye opens "Ver como visitante" (mockup v3 P8, C6), which never counts a visit.
 *
 * **One editor, keyed by the link's id** (checklist 14): opening another link's editor mounts a fresh
 * one, so a typed value never carries over, and an answer that lands after its editor is gone is
 * ignored instead of closing or annotating the editor the person is now in.
 */

import { useEffect, useRef, useState } from "react";
import type { ReactElement } from "react";
import { Link, useNavigate } from "react-router";

import { useTranslation } from "../../../shared/i18n";
import { Button, buttonClassName } from "../../../shared/ui/atoms/Button";
import { Icon } from "../../../shared/ui/atoms/Icon";
import { Select } from "../../../shared/ui/atoms/Select";
import { ConfirmDialog } from "../../../shared/ui/molecules/ConfirmDialog";
import { EmptyState } from "../../../shared/ui/molecules/EmptyState";
import { ErrorState } from "../../../shared/ui/molecules/ErrorState";
import { TableSkeleton } from "../../../shared/ui/molecules/TableSkeleton";
import { Tabs, tabPanelProps } from "../../../shared/ui/molecules/Tabs";
import { userMessage } from "../../../shared/ui/molecules/userMessage";
import { Dialog } from "../../../shared/ui/organisms/Dialog";
import type { EditShareLinkInput, PanelLinkView } from "../../../types/sharing";
import { LinkEditor, freshEdit } from "../components/LinkEditor";
import type { LinkEditDraft } from "../components/LinkEditor";
import { LinksTable } from "../components/LinksTable";
import styles from "../components/ShareDialog.module.css";
import { linkName } from "../components/ShareLinkList";
import { useEditShareLink, useMyLinks, useRevokeShareLink } from "../hooks/useShareLinks";
import { useVisitorPreview } from "../hooks/useVisitorPreview";
import { visitorTargetOf } from "../model/visitorTarget";
import { expiryInput } from "../model/expiryDays";
import panelStyles from "./SharingPanelContainer.module.css";
import { VisitorPreviewContainer, visitorKey } from "./VisitorPreviewContainer";

/** The refusals of the edit route whose sentence the server writes for people: a revoked link. */
const EDIT_REFUSAL_CODES = ["CONFLICT"] as const;

/** The refusals of a rename whose sentence the server writes for people: a revoked link, a long label. */
const RENAME_REFUSAL_CODES = ["CONFLICT", "VALIDATION"] as const;

const TABS_ID = "sharing-state";
type StateTab = "all" | "active" | "expired" | "revoked";
const ARTIFACT_FIELD_ID = "sharing-artifact-filter";

export function SharingPanelContainer(): ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const links = useMyLinks();
  const revoke = useRevokeShareLink();
  const edit = useEditShareLink();
  const visitor = useVisitorPreview();
  const [tab, setTab] = useState<StateTab>("all");
  const [artifact, setArtifact] = useState("all");
  const [editing, setEditing] = useState<PanelLinkView | null>(null);
  const [revoking, setRevoking] = useState<PanelLinkView | null>(null);
  const [revokePending, setRevokePending] = useState<string | null>(null);
  const [revokeRefusal, setRevokeRefusal] = useState<string | undefined>(undefined);

  const confirmRevoke = async (link: PanelLinkView): Promise<void> => {
    setRevoking(null);
    setRevokePending(link.id);
    setRevokeRefusal(undefined);
    const outcome = await revoke(link.mockup_id, link.id);
    setRevokePending(null);
    if (!outcome.ok) setRevokeRefusal(userMessage(outcome.error, t("sharing.revoke.failed")));
  };

  const rename = async (link: PanelLinkView, label: string): Promise<string | null> => {
    const outcome = await edit(link.mockup_id, link.id, { label });
    return outcome.ok ? null : userMessage(outcome.error, t("sharing.label.failed"), RENAME_REFUSAL_CODES);
  };

  const rows = links.data?.pages.flatMap((page) => page.links);
  const complete = rows !== undefined && !links.hasNextPage;
  const countOf = (state: string | null): number | undefined =>
    !complete ? undefined : rows.filter((row) => state === null || row.state === state).length;
  const shown = (rows ?? []).filter(
    (row) => (tab === "all" || row.state === tab) && (artifact === "all" || row.artifact === artifact),
  );

  let body: ReactElement;
  if (links.isError && rows === undefined) {
    body = (
      <ErrorState
        error={links.error}
        onRetry={() => {
          void links.refetch();
        }}
      />
    );
  } else if (rows === undefined) {
    body = <TableSkeleton />;
  } else if (rows.length === 0) {
    body = (
      <EmptyState
        icon="link"
        title={t("sharing.empty.title")}
        body={t("sharing.empty.body")}
        action={
          <Link className={buttonClassName("primary")} to="/">
            <Icon name="grid" size="sm" />
            {t("sharing.empty.action")}
          </Link>
        }
      />
    );
  } else {
    body = (
      <>
        {shown.length === 0 ? (
          <EmptyState icon="link" title={t("sharing.noState.title")} body={t("sharing.noState.body")} />
        ) : (
          <LinksTable
            links={shown}
            pendingId={revokePending}
            onRevoke={setRevoking}
            onEdit={setEditing}
            onOpenMockup={(link) => {
              void navigate(`/lienzo/${encodeURIComponent(link.mockup_id)}`);
            }}
            onRename={rename}
            onVisitor={(link) => {
              visitor.open(visitorTargetOf(link, linkName(link, t("sharing.list.noLabel"))));
            }}
          />
        )}
        {links.isError ? (
          <ErrorState
            error={links.error}
            onRetry={() => {
              void (links.isFetchNextPageError ? links.fetchNextPage() : links.refetch());
            }}
          />
        ) : null}
        {links.hasNextPage ? (
          <div className={styles.actions}>
            <Button
              variant="secondary"
              loading={links.isFetchingNextPage}
              disabled={links.isFetchingNextPage}
              onClick={() => {
                void links.fetchNextPage();
              }}
            >
              {t("sharing.panel.more")}
            </Button>
          </div>
        ) : null}
      </>
    );
  }

  const hasRows = rows !== undefined && rows.length > 0;

  return (
    <section className={panelStyles.panel} aria-label={t("sharing.panel.title")}>
      {revokeRefusal === undefined ? null : (
        <p role="alert" className={styles.refusal}>
          {revokeRefusal}
        </p>
      )}
      {hasRows ? (
        <div className={panelStyles.toolbar}>
          <Tabs
            label={t("sharing.tabs.label")}
            idBase={TABS_ID}
            items={[
              { id: "all", label: t("sharing.tab.all"), count: countOf(null) },
              { id: "active", label: t("sharing.tab.active"), count: countOf("active") },
              { id: "expired", label: t("sharing.tab.expired"), count: countOf("expired") },
              { id: "revoked", label: t("sharing.tab.revoked"), count: countOf("revoked") },
            ]}
            selected={tab}
            onSelect={(id) => {
              setTab(id === "active" || id === "expired" || id === "revoked" ? id : "all");
            }}
          />
          <span className={panelStyles.spacer} />
          <span className={panelStyles.artifact}>
            <Select
              id={ARTIFACT_FIELD_ID}
              label={t("sharing.col.artifact")}
              value={artifact}
              options={[
                { value: "all", label: t("sharing.artifact.all") },
                { value: "prototype", label: t("sharing.artifact.prototype") },
                { value: "diagram", label: t("sharing.artifact.diagram") },
              ]}
              onChange={setArtifact}
            />
            </span>
        </div>
      ) : null}
      <div className={panelStyles.content} {...(hasRows ? tabPanelProps(TABS_ID, tab) : {})}>
        {body}
      </div>
      {editing === null ? null : (
        <Dialog
          open
          title={t("sharing.edit.title", { name: linkName(editing, t("sharing.list.noLabel")) })}
          onClose={() => {
            setEditing(null);
          }}
        >
          <EditSection
            key={editing.id}
            link={editing}
            onDone={() => {
              setEditing(null);
            }}
          />
        </Dialog>
      )}
      {visitor.target === null ? null : (
        <VisitorPreviewContainer key={visitorKey(visitor.target)} target={visitor.target} onClose={visitor.close} />
      )}
      <ConfirmDialog
        open={revoking !== null}
        title={t("sharing.revoke.title")}
        body={t("sharing.revoke.body")}
        confirmLabel={t("sharing.revoke")}
        cancelLabel={t("common.action.cancel")}
        onConfirm={() => {
          if (revoking !== null) void confirmRevoke(revoking);
        }}
        onCancel={() => {
          setRevoking(null);
        }}
      />
    </section>
  );
}

interface EditSectionProps {
  readonly link: PanelLinkView;
  readonly onDone: () => void;
}

/**
 * What the draft asks the server to change, or that it changes nothing. "1 año" is a new year
 * from the edit (owner decision 2026-10-19), so choosing it again is a change like any other.
 */
function editOf(draft: LinkEditDraft): EditShareLinkInput | "nothing" {
  const input: { expires_in_days?: number; never_expires?: true; password?: string | null } =
    draft.expiry === "keep" ? {} : expiryInput(draft.expiry);
  if (draft.clearPassword) input.password = null;
  else if (draft.password !== "") input.password = draft.password;
  return Object.keys(input).length === 0 ? "nothing" : input;
}

/** The editor of ONE link; the panel mounts it with `key` = the link id. */
function EditSection({ link, onDone }: EditSectionProps): ReactElement {
  const { t } = useTranslation();
  const edit = useEditShareLink();
  const [draft, setDraft] = useState<LinkEditDraft>(() => freshEdit());
  const [pending, setPending] = useState(false);
  const [refusal, setRefusal] = useState<string | undefined>(undefined);
  // The synchronous lock of the write (checklist 13): `pending` only changes after a render.
  const sending = useRef(false);
  // An answer that lands after this editor is gone belongs to nothing on screen.
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const submit = async (): Promise<void> => {
    if (sending.current) return;
    const input = editOf(draft);
    setRefusal(input === "nothing" ? t("sharing.edit.nothing") : undefined);
    if (input === "nothing") return;
    sending.current = true;
    setPending(true);
    const outcome = await edit(link.mockup_id, link.id, input);
    sending.current = false;
    if (!mounted.current) return;
    setPending(false);
    if (outcome.ok) {
      onDone();
      return;
    }
    setRefusal(
      outcome.error.status === 503
        ? t("sharing.disabled")
        : userMessage(outcome.error, t("sharing.edit.failed"), EDIT_REFUSAL_CODES),
    );
  };

  return (
    <LinkEditor
      name={linkName(link, t("sharing.list.noLabel"))}
      hasPassword={link.has_password}
      draft={draft}
      onChange={(patch) => {
        setDraft((current) => ({ ...current, ...patch }));
      }}
      refusal={refusal}
      pending={pending}
      onSubmit={() => {
        void submit();
      }}
      onCancel={onDone}
    />
  );
}
